// Busca unificada de clientes e veiculos para o modulo de Checklist.
//
// O problema que isto resolve: o Checklist tem a sua propria tabela
// (checklist_clientes) e o painel de gestao tem outra (clientes). Ate aqui
// cada tela enxergava so metade da oficina -- quem estava cadastrado no
// painel simplesmente nao aparecia no Checklist, e vice-versa.
//
// Este servico le as DUAS fontes e devolve uma lista unica, ja mesclada.
// Nao grava, nao migra, nao altera nem apaga nada em nenhuma das duas:
// e leitura adicional e mais nada. As tabelas continuam como estao.
//
// Identificadores: o campo clienteId/veiculoId de um checklist referencia
// um registro de checklist_clientes. Uma pessoa que so existe no painel
// NAO tem esse id -- para ela devolvemos checklistId = null e o formulario
// apenas preenche os campos. Nunca gravamos um id do painel num campo que
// aponta para a tabela do Checklist.
import API_BASE from "../../config.js";
import { authFetch } from "../../services/authFetch";
import { clienteService, veiculoService } from "./cadastroService";

export function normalizar(s) {
  return String(s == null ? "" : s)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

export function soDigitos(s) {
  return String(s == null ? "" : s).replace(/\D+/g, "");
}

// Placa entra de varios jeitos: "ABC-1D23", "abc1d23", "ABC 1D23".
// Comparamos sempre sem hifen, sem espaco e em caixa baixa.
export function chavePlaca(s) {
  return normalizar(s).replace(/[^a-z0-9]/g, "");
}

// O painel guarda os veiculos dentro do proprio cliente. Dependendo de como
// a linha volta do banco isso pode chegar como array ou como texto JSON --
// aceitamos os dois e, em qualquer outro caso, devolvemos lista vazia em vez
// de deixar a tela quebrar.
function listaDeVeiculos(bruto) {
  if (Array.isArray(bruto)) return bruto;
  if (typeof bruto === "string" && bruto.trim()) {
    try {
      const v = JSON.parse(bruto);
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  }
  return [];
}

function veiculoNormalizado(v, origem) {
  return {
    origem,
    id: v && v.id ? v.id : null,
    placa: String((v && v.placa) || "").toUpperCase(),
    marca: (v && v.marca) || "",
    modelo: (v && v.modelo) || "",
    ano: (v && v.ano) || "",
    km: (v && v.km) || "",
    cor: (v && v.cor) || "",
  };
}

function rotuloVeiculo(v) {
  const desc = [v.marca, v.modelo, v.ano].filter(Boolean).join(" ").trim();
  if (v.placa && desc) return v.placa + " · " + desc;
  return v.placa || desc || "";
}

export { rotuloVeiculo };

// Clientes do painel de gestao. Mesmo tenant, mesmo token: workshopId e o
// tenantId (ver App.js, onde o WorkshopProvider recebe getTenantIdFromToken).
// Se a chamada falhar por qualquer motivo, devolvemos lista vazia -- a busca
// continua funcionando so com os dados do Checklist, como era antes.
async function clientesDoPainel(workshopId) {
  try {
    const r = await authFetch(API_BASE + "/api/v1/dados/clientes/" + workshopId, {});
    if (!r.ok) return [];
    const d = await r.json();
    return Array.isArray(d) ? d : [];
  } catch (err) {
    console.error("Busca unificada: falha ao ler clientes do painel:", err);
    return [];
  }
}

// Carrega e mescla as duas fontes.
// Devolve: [{ chave, nome, telefone, cpf, email, checklistId, origens, veiculos }]
export async function carregarPessoas(workshopId) {
  const [doChecklist, veiculosChecklist, doPainel] = await Promise.all([
    clienteService.list(workshopId),
    veiculoService.list(workshopId),
    clientesDoPainel(workshopId),
  ]);

  const pessoas = [];
  const porNome = new Map();

  function fundir(alvo, extra) {
    if (!alvo.telefone && extra.telefone) alvo.telefone = extra.telefone;
    if (!alvo.cpf && extra.cpf) alvo.cpf = extra.cpf;
    if (!alvo.email && extra.email) alvo.email = extra.email;
    // So o id do Checklist serve para vincular o registro.
    if (!alvo.checklistId && extra.checklistId) alvo.checklistId = extra.checklistId;
    extra.origens.forEach((o) => {
      if (alvo.origens.indexOf(o) === -1) alvo.origens.push(o);
    });
    extra.veiculos.forEach((v) => {
      const k = chavePlaca(v.placa);
      const repetido = k && alvo.veiculos.some((x) => chavePlaca(x.placa) === k);
      if (!repetido) alvo.veiculos.push(v);
    });
  }

  function adicionar(p) {
    const nomeK = normalizar(p.nome);
    if (!nomeK) return;
    const irmaos = porNome.get(nomeK) || [];
    const telP = soDigitos(p.telefone).slice(-8);
    // Mesmo nome funde quando os telefones sao compativeis: iguais, ou um
    // dos dois em branco. Dois telefones diferentes = duas pessoas, e
    // aparecem separadas na lista (com o telefone a vista para desempatar).
    const igual = irmaos.find((q) => {
      const telQ = soDigitos(q.telefone).slice(-8);
      return !telP || !telQ || telP === telQ;
    });
    if (igual) {
      fundir(igual, p);
      return;
    }
    irmaos.push(p);
    porNome.set(nomeK, irmaos);
    pessoas.push(p);
  }

  // Primeiro o Checklist, para que o checklistId seja preservado na fusao.
  doChecklist.forEach((c) => {
    const meus = veiculosChecklist.filter((v) => v.clienteId === c.id);
    adicionar({
      chave: "chk:" + c.id,
      nome: c.nome || "",
      telefone: c.telefone || "",
      cpf: c.cpf || "",
      email: c.email || "",
      checklistId: c.id || null,
      origens: ["checklist"],
      veiculos: meus.map((v) => veiculoNormalizado(v, "checklist")),
    });
  });

  doPainel.forEach((c) => {
    adicionar({
      chave: "pnl:" + c.id,
      nome: c.nome || "",
      telefone: c.telefone || "",
      cpf: c.cpf || "",
      email: c.email || "",
      checklistId: null,
      origens: ["painel"],
      veiculos: listaDeVeiculos(c.veiculos).map((v) => veiculoNormalizado(v, "painel")),
    });
  });

  pessoas.sort((a, b) => normalizar(a.nome).localeCompare(normalizar(b.nome)));
  return pessoas;
}

// Filtra por nome, telefone, CPF, placa, marca ou modelo.
// Pontuacao: quem comeca com o termo (ou tem a placa exata) vem primeiro.
export function filtrarPessoas(pessoas, termo, limite) {
  const alvo = normalizar(termo);
  if (alvo.length < 2) return [];
  const digitos = soDigitos(termo);
  const placa = chavePlaca(termo);
  const max = limite || 8;
  const achados = [];

  pessoas.forEach((p) => {
    const nome = normalizar(p.nome);
    const tel = soDigitos(p.telefone);
    const cpf = soDigitos(p.cpf);
    let pontos = -1;

    if (nome.indexOf(alvo) === 0) pontos = 0;
    else if (nome.indexOf(alvo) > 0) pontos = 2;

    if (placa.length >= 3) {
      p.veiculos.forEach((v) => {
        const k = chavePlaca(v.placa);
        if (!k) return;
        if (k === placa) pontos = 0;
        else if (k.indexOf(placa) === 0 && pontos !== 0) pontos = 1;
        else if (k.indexOf(placa) > 0 && pontos === -1) pontos = 3;
      });
    }

    if (pontos === -1 && digitos.length >= 3) {
      if (tel && tel.indexOf(digitos) !== -1) pontos = 1;
      else if (cpf && cpf.indexOf(digitos) !== -1) pontos = 1;
    }

    if (pontos === -1) {
      const bate = p.veiculos.some((v) => {
        const desc = normalizar(v.marca + " " + v.modelo);
        return desc.indexOf(alvo) !== -1;
      });
      if (bate) pontos = 3;
    }

    if (pontos >= 0) achados.push({ p: p, pontos: pontos });
  });

  achados.sort((a, b) => a.pontos - b.pontos || normalizar(a.p.nome).localeCompare(normalizar(b.p.nome)));
  return achados.slice(0, max).map((x) => x.p);
}
